import { useEffect, useRef, useState } from 'react';
import './PhotoViewer.css';

const SWIPE_THRESHOLD = 50;

// 사진 크게 보기 — 좌우 스와이프로 순환 (마지막 → 처음)
export default function PhotoViewer({ photos, startIndex = 0, onClose }) {
  const count = photos.length;
  const loop = count > 1;
  // 순환을 위해 양 끝에 복제 슬라이드 배치: [마지막, ...photos, 처음]
  const slides = loop ? [photos[count - 1], ...photos, photos[0]] : photos;
  const [pos, setPos] = useState(loop ? startIndex + 1 : startIndex);
  const [animating, setAnimating] = useState(false);
  const [dragX, setDragX] = useState(0);
  const dragStartRef = useRef(null);
  const movedRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const current = loop ? (pos - 1 + count) % count : pos;

  // 뒤로가기 버튼으로 뷰어 닫기
  useEffect(() => {
    if (!window.history.state?.lifelogViewer) {
      window.history.pushState({ ...window.history.state, lifelogViewer: true }, '');
    }
    const handlePop = () => onCloseRef.current();
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  const close = () => {
    if (window.history.state?.lifelogViewer) window.history.back();
    else onClose();
  };

  const go = (dir) => {
    if (!loop || animating) return;
    setAnimating(true);
    setPos((p) => p + dir);
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  const handleTransitionEnd = () => {
    setAnimating(false);
    // 복제 슬라이드에 도착하면 애니메이션 없이 실제 위치로 점프
    if (pos === 0) setPos(count);
    else if (pos === count + 1) setPos(1);
  };

  const handlePointerDown = (e) => {
    if (animating) return;
    dragStartRef.current = e.clientX;
    movedRef.current = false;
  };

  const handlePointerMove = (e) => {
    if (dragStartRef.current === null) return;
    const dx = e.clientX - dragStartRef.current;
    if (Math.abs(dx) > 5) movedRef.current = true;
    if (loop) setDragX(dx);
  };

  const handlePointerEnd = (e) => {
    if (dragStartRef.current === null) return;
    const dx = e.clientX - dragStartRef.current;
    dragStartRef.current = null;
    setDragX(0);
    if (!loop || dx === 0) return;
    if (Math.abs(dx) > SWIPE_THRESHOLD) {
      go(dx < 0 ? 1 : -1);
    } else {
      setAnimating(true); // 제자리로 되돌아가는 애니메이션
    }
  };

  const handleClick = (e) => {
    // 스와이프 후 click, 사진 자체 탭은 무시 — 빈 영역 탭만 닫기
    if (movedRef.current) {
      movedRef.current = false;
      return;
    }
    if (e.target.tagName !== 'IMG') close();
  };

  return (
    <div
      className="pv-backdrop"
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerLeave={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
    >
      <div
        className="pv-track"
        style={{
          transform: `translateX(calc(${-pos * 100}% + ${dragX}px))`,
          transition: animating ? 'transform 0.3s ease' : 'none',
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        {slides.map((url, i) => (
          <div key={i} className="pv-slide">
            <img src={url} alt={`사진 ${current + 1}`} draggable={false} />
          </div>
        ))}
      </div>

      <button className="pv-close" onClick={(e) => { e.stopPropagation(); close(); }} aria-label="닫기">×</button>

      {loop && (
        <>
          <span className="pv-counter">{current + 1} / {count}</span>
          <button className="pv-nav pv-nav--prev" onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="이전 사진">‹</button>
          <button className="pv-nav pv-nav--next" onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="다음 사진">›</button>
          <div className="pv-dots">
            {photos.map((_, i) => (
              <span key={i} className={`pv-dot${i === current ? ' pv-dot--active' : ''}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
